import { forwardRef, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes, useId } from 'react'
import clsx from 'clsx'

/**
 * Ties a field to its label and to the message under it, so a screen reader
 * announces "Email address" and the error rather than an unnamed box. The
 * labels used to be free-standing: nothing named the field.
 */
function useField(id: string | undefined, error?: string, hint?: string, describedBy?: string) {
  const generated = useId()
  const fieldId = id ?? generated
  const messageId = error || hint ? `${fieldId}-message` : undefined
  return {
    fieldId,
    messageId,
    aria: {
      id: fieldId,
      'aria-invalid': error ? true : undefined,
      'aria-describedby': [describedBy, messageId].filter(Boolean).join(' ') || undefined,
    },
  }
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  /** Interactive trailing element (e.g. a show/hide password toggle). Unlike
   *  `rightIcon` it receives pointer events. */
  rightAdornment?: React.ReactNode
}

/**
 * Brand-styled input component
 * - 8px border radius
 * - Teal focus ring
 * - Inter font for form elements
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftIcon, rightIcon, rightAdornment, className, id, ...props }, ref) => {
    const { fieldId, messageId, aria } = useField(id, error, hint, props['aria-describedby'])
    return (
      <div className="w-full">
        {label && (
          <label className="label" htmlFor={fieldId}>
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-ink-faint">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            className={clsx(
              'w-full bg-surface px-4 py-3 border rounded-input',
              'font-inter text-body text-ink',
              'placeholder:text-ink-faint',
              'focus:outline-none focus:ring-1 transition-colors duration-200',
              error
                ? 'border-danger focus:border-danger focus:ring-danger'
                : 'border-hairline focus:border-accent focus:ring-accent',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              rightAdornment && 'pr-11',
              className
            )}
            {...props}
            {...aria}
          />
          {rightAdornment ? (
            <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
              {rightAdornment}
            </div>
          ) : rightIcon ? (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-ink-faint">
              {rightIcon}
            </div>
          ) : null}
        </div>
        {error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-danger">{error}</p>
        )}
        {hint && !error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-ink-faint">{hint}</p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  hint?: string
  options: { value: string; label: string }[]
  placeholder?: string
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, hint, options, placeholder, className, id, ...props }, ref) => {
    const { fieldId, messageId, aria } = useField(id, error, hint, props['aria-describedby'])
    return (
      <div className="w-full">
        {label && (
          <label className="label" htmlFor={fieldId}>
            {label}
          </label>
        )}
        <select
          ref={ref}
          className={clsx(
            'w-full bg-surface px-4 py-3 border rounded-input appearance-none',
            'font-inter text-body text-ink',
            'focus:outline-none focus:ring-1 transition-colors duration-200',
            'bg-surface bg-[url("data:image/svg+xml,%3csvg xmlns=%27http://www.w3.org/2000/svg%27 fill=%27none%27 viewBox=%270 0 20 20%27%3e%3cpath stroke=%27%235C6B73%27 stroke-linecap=%27round%27 stroke-linejoin=%27round%27 stroke-width=%271.5%27 d=%27M6 8l4 4 4-4%27/%3e%3c/svg%3e")]',
            'bg-[length:1.5rem_1.5rem] bg-[right_0.75rem_center] bg-no-repeat',
            error
              ? 'border-danger focus:border-danger focus:ring-danger'
              : 'border-hairline focus:border-accent focus:ring-accent',
            className
          )}
          {...props}
          {...aria}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-danger">{error}</p>
        )}
        {hint && !error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-ink-faint">{hint}</p>
        )}
      </div>
    )
  }
)

Select.displayName = 'Select'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, hint, className, id, ...props }, ref) => {
    const { fieldId, messageId, aria } = useField(id, error, hint, props['aria-describedby'])
    return (
      <div className="w-full">
        {label && (
          <label className="label" htmlFor={fieldId}>
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          className={clsx(
            'w-full bg-surface px-4 py-3 border rounded-input resize-y min-h-[100px]',
            'font-inter text-body text-ink',
            'placeholder:text-ink-faint',
            'focus:outline-none focus:ring-1 transition-colors duration-200',
            error
              ? 'border-danger focus:border-danger focus:ring-danger'
              : 'border-hairline focus:border-accent focus:ring-accent',
            className
          )}
          {...props}
          {...aria}
        />
        {error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-danger">{error}</p>
        )}
        {hint && !error && (
          <p id={messageId} className="mt-1.5 font-inter text-body-sm text-ink-faint">{hint}</p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
